import { DataSourceType, IQuery, IDataSource, ISQLDataSource, ICollectionDataSource, IStep, StepType, IQueryBuilderContext, IRegisteredStep, IStepValidationResponse, ISourceSchema, IDSField, ISourceOrigins, IWorkflowDataSource, IDXDataSource, IQueryDataSource, IDataSourceMetadata, FilteredOrigins, IDataSourceBaseProps, Param } from "@types";
import axios, { AxiosRequestConfig } from "axios";
import { CatalogDefinition, getAvailableSources, getSchema } from "@query-engine-service";
import { updateSource, updateStep } from "@components/StepConfigurator/StepConfigUtils";
import { hasValue } from "uxp/components";

export const InitialSourceId = '__initial_source__';
export const SourceOriginSQL = 'SQL Tables'
export const SourceOriginQuery = 'Saved Queries'

export function trimTrailingSlashes(str: string) {
    return str.replace(/\/+$/, '');
}

export enum Method {
    GET = 'get',
    POST = 'post',
    PUT = 'put',
    PATCH = 'patch',
    DELETE = 'delete'
}

function getBaseURL(context: IQueryBuilderContext) {
    // return 'http://localhost:21000'
    const lucyUrl = trimTrailingSlashes(context?.uxpContext?.lucyUrl || '/')
    const microServiceName = 'QueryEngine'
    return `${lucyUrl}/components/${microServiceName}`
}
export function getEndPointUrl(context: IQueryBuilderContext, endpoint: string): string {
    const base = trimTrailingSlashes(getBaseURL(context))

    if (!endpoint.startsWith('/')) endpoint = '/' + endpoint
    const rest = trimTrailingSlashes(endpoint)
    return `${base}${rest}`;
}

export async function executeRequest(context: IQueryBuilderContext, endpoint: string, method: Method, params?: any, body?: any): Promise<{ data: any, error?: string }> {

    const url = getEndPointUrl(context, endpoint);

    const config: AxiosRequestConfig = {
        method: method,
        url: url,
        headers: {
            'Authorization': `APIKEY ${context?.uxpContext.apiKey}`,
            'Content-Type': 'application/json',
        }
    };

    if ([Method.POST, Method.PATCH, Method.PUT].includes(method)) {
        config.data = body || {}
    }
    config.params = params || {}

    try {
        const response = await axios(config);
        return { data: response.data };
    } catch (e) {
        console.log('Request Failed. error: ', e)
        const errorObj = e?.response?.data || e;
        let errorMessage = 'Request failed';

        // Handle JSON error response
        if (errorObj?.message) {
            errorMessage = errorObj.message;
        }
        // Handle legacy error field
        else if (errorObj?.error) {
            errorMessage = errorObj.error;
        }
        // Handle HTML error response
        else if (typeof errorObj === 'string' && errorObj.includes('<html')) {
            const preMatch = errorObj.match(/<pre>(.*?)<\/pre>/);
            if (preMatch) {
                let content = preMatch[1].trim();
                // Remove HTML entities and tags
                content = content.replace(/&nbsp;/g, ' ').replace(/<br\s*\/?>/g, '\n').replace(/<[^>]*>/g, '');
                // Extract only the first line (main error message)
                const firstLine = content.split('\n')[0].trim();
                errorMessage = firstLine || content;
            } else {
                // Fallback: extract text between body tags
                const bodyMatch = errorObj.match(/<body[^>]*>(.*?)<\/body>/);
                if (bodyMatch) {
                    errorMessage = bodyMatch[1].replace(/<[^>]*>/g, '').trim();
                }
            }
        }
        // Handle plain string errors
        else if (typeof errorObj === 'string') {
            errorMessage = errorObj;
        }
        // Handle error objects with toString
        else if (errorObj?.toString && typeof errorObj.toString === 'function') {
            errorMessage = errorObj.toString();
        }

        return { data: null, error: errorMessage };
    }
}


const RegisteredSteps: { [type: string]: IRegisteredStep } = {}

export async function handleStepFieldValueChanges(context: IQueryBuilderContext, query: IQuery, selectedStepId: string, field: string, value: any, source?: boolean): Promise<IQuery> {
    let _query = (source ? updateSource : updateStep)(query, selectedStepId, field, value)

    if (source) {
        const sourceId = (value as IDataSource).id
        //since current step is not configured properly get query up to previous step to get the schema
        const tempQuery = getQueryUntilPreviousStep(_query, selectedStepId)
        const schema = await (isDataSource(value) ? getSchemaForSource : getSchemaForStep)(context, tempQuery, sourceId)
        _query = appendSchema(_query, schema, sourceId, true)
    }

    return _query
}

export async function handleMultipleStepFieldValueChanges(context: IQueryBuilderContext, query: IQuery, selectedStepId: string, updatedValues: { field: string, value: any, source?: boolean }[]): Promise<IQuery> {
    for (const v of updatedValues) {
        query = await handleStepFieldValueChanges(context, query, selectedStepId, v.field, v.value, v.source)
    }
    return query
}

export function registerStep(step: IRegisteredStep) {
    const type = (step.type as unknown) as string
    if (!RegisteredSteps[type]) {
        RegisteredSteps[type] = step
    }
}

export function getRegisteredStepByType(type: StepType) {
    if (!type) return null
    const sType = (type as unknown) as string
    const step = RegisteredSteps[sType]
    return step
}

export function getRegisteredSteps() {
    return Object.values(RegisteredSteps)
}

export function getDatasourceTypeFromId(id: string): DataSourceType | null {
    if (!id) return null;
    if (id.startsWith('sqltable')) return DataSourceType.SQL;
    if (id.startsWith('dx')) return DataSourceType.DX;
    else if (id.startsWith('collection')) return DataSourceType.COLLECTION;
    else if (id.startsWith('json')) return DataSourceType.JSON;
    else if (id.startsWith('lucy')) return DataSourceType.WORKFLOW;
    else if (id.startsWith('query')) return DataSourceType.Query;
    return null;
}

export function getSourceMetadataFromId(id: string): { type: DataSourceType | null, metadata: IDataSourceMetadata | null, category?: string } {
    const type = getDatasourceTypeFromId(id);
    if (!hasValue(type)) return { type: null, metadata: null };
    if (type == DataSourceType.JSON) return { type, metadata: null, category: 'JSON' };

    const metadataString = id.split(':')[1];
    if (!hasValue(metadataString)) return { type: null, metadata: null, category: '' };

    if (type == DataSourceType.SQL) return { type, metadata: { table: metadataString }, category: 'Sql Table' };

    if (type == DataSourceType.DX) return { type, metadata: { table: metadataString }, category: 'DX App' };

    const [model, collection] = metadataString.split('/');
    if (!hasValue(model) || !hasValue(collection)) return { type: null, metadata: null, category: '' };
    if (type == DataSourceType.COLLECTION) return { type, metadata: { model, collection }, category: model };
    if (type == DataSourceType.Query) return { type, metadata: { queryName: model, queryId: collection } }
    return { type, metadata: { model, action: collection }, category: model };
}

export function hasHistoryEnabledForField(dataSource: IDataSource, field: string): boolean {
    const historyFields = getHistoryEnabledFields(dataSource)
    return ((historyFields?.map(f => (f.name))).includes(field))
}

export function getHistoryEnabledFields(dataSource: IDataSource): IDSField[] {
    return (dataSource?.fields || [])?.filter(f => (f?.hasOwnProperty('historicalData') && f?.historicalData && Object.keys(f.historicalData)?.length > 0))
}

export function hasHistoryEnabledForSource(dataSource: IDataSource): boolean {
    const historyFields = getHistoryEnabledFields(dataSource)
    return historyFields?.length > 0
}

function isValidOrigin(id: string, origins: FilteredOrigins): boolean {
    if (!id || !origins || origins?.length == 0) return true
    const origin = id.split(":")?.[0]
    if (!origin) return false
    return origins.includes(origin as any)
}

// export function prepareSources(dataSources: any[], origins: FilteredOrigins): { sources: IDataSource[], origins: ISourceOrigins } {
//     const sourceOrigins: ISourceOrigins = {}

//     const sources = dataSources
//         .filter(dataSource => {
//             return isValidOrigin(dataSource.source, origins)
//         })
//         .map(dataSource => {
//             dataSource.id = dataSource.source

//             const metadata = getSourceMetadataFromId(dataSource.source);
//             if (metadata) {
//                 dataSource.type = metadata.type;
//                 if (metadata.metadata) {
//                     const { model, action, collection, table, queryName, queryId } = metadata.metadata
//                     if (metadata.type === DataSourceType.SQL) {
//                         (dataSource as ISQLDataSource).source = table;
//                         dataSource.name = table;
//                         dataSource.origin = SourceOriginSQL;

//                         if (!sourceOrigins[SourceOriginSQL]) sourceOrigins[SourceOriginSQL] = { sources: [], types: [DataSourceType.SQL] };
//                         sourceOrigins[SourceOriginSQL].sources.push(dataSource.id);

//                     } else if (metadata.type === DataSourceType.DX) {

//                         (dataSource as IDXDataSource).source = table;
//                         dataSource.name = table
//                         dataSource.origin = SourceOriginSQL
//                         if (!sourceOrigins[SourceOriginSQL]) sourceOrigins[SourceOriginSQL] = { sources: [], types: [DataSourceType.DX] }
//                         sourceOrigins[SourceOriginSQL].sources.push(dataSource.id)

//                     } else if (metadata.type === DataSourceType.COLLECTION) {

//                         (dataSource as ICollectionDataSource).model = model;
//                         (dataSource as ICollectionDataSource).collection = collection;
//                         dataSource.name = collection;
//                         dataSource.origin = model;

//                         if (!sourceOrigins[model]) sourceOrigins[model] = { sources: [], types: [DataSourceType.COLLECTION] }
//                         sourceOrigins[model].sources.push(dataSource.id);
//                         if (!sourceOrigins[model].types.includes(DataSourceType.COLLECTION)) sourceOrigins[model].types.push(DataSourceType.COLLECTION);
//                     }
//                     else if (metadata.type === DataSourceType.WORKFLOW) {

//                         (dataSource as IWorkflowDataSource).model = model;
//                         (dataSource as IWorkflowDataSource).action = action;
//                         dataSource.name = action

//                         dataSource.origin = model

//                         if (!sourceOrigins[model]) sourceOrigins[model] = { sources: [], types: [DataSourceType.WORKFLOW] }
//                         sourceOrigins[model].sources.push(dataSource.id)
//                         if (!sourceOrigins[model].types.includes(DataSourceType.WORKFLOW)) sourceOrigins[model].types.push(DataSourceType.WORKFLOW)

//                     }
//                     else if (metadata.type == DataSourceType.Query) {
//                         dataSource.name = queryName;
//                         (dataSource as IQueryDataSource).queryName = queryName;
//                         (dataSource as IQueryDataSource).queryId = queryId;

//                         dataSource.origin = SourceOriginQuery
//                         if (!sourceOrigins[SourceOriginQuery]) sourceOrigins[SourceOriginQuery] = { sources: [], types: [DataSourceType.Query] };
//                         sourceOrigins[SourceOriginQuery].sources.push(dataSource.id);
//                     }
//                 }
//             }

//             dataSource.history = hasHistoryEnabledForSource(dataSource)

//             return dataSource;
//         });

//     return { sources: sources, origins: sourceOrigins }
// }

export function prepareSources(catalogs: CatalogDefinition[], origins: FilteredOrigins): { sources: IDataSource[], origins: ISourceOrigins } {
    const sourceOrigins: ISourceOrigins = {}
    const sources = catalogs
        .reduce((result, catalog) => {
            const { source, description, fields, params } = catalog

            if (!isValidOrigin(source, origins)) return result;

            const metadata = getSourceMetadataFromId(source);
            if (!metadata || !metadata.metadata) return result;

            const dataSource: IDataSourceBaseProps = {
                id: source,
                source: source,
                description: description,
                name: source,
                fields: fields,
                schema: fields as ISourceSchema,
                params: params || []
            };

            (dataSource as IDataSource).type = metadata.type;

            const { model, action, collection, table, queryName, queryId } = metadata.metadata;

            if (metadata.type === DataSourceType.SQL) {
                (dataSource as ISQLDataSource).source = table;
                dataSource.name = table;
                dataSource.origin = SourceOriginSQL;
                if (!sourceOrigins[SourceOriginSQL]) sourceOrigins[SourceOriginSQL] = { sources: [], types: [DataSourceType.SQL] };
                sourceOrigins[SourceOriginSQL].sources.push(dataSource.id);
            }
            else if (metadata.type === DataSourceType.DX) {
                (dataSource as IDXDataSource).source = table;
                dataSource.name = table
                dataSource.origin = table
                if (!sourceOrigins[table]) sourceOrigins[table] = { sources: [], types: [DataSourceType.DX] }
                sourceOrigins[table].sources.push(dataSource.id)
            }
            else if (metadata.type === DataSourceType.COLLECTION) {
                (dataSource as ICollectionDataSource).model = model;
                (dataSource as ICollectionDataSource).collection = collection;
                dataSource.name = collection;
                dataSource.origin = model;
                if (!sourceOrigins[model]) sourceOrigins[model] = { sources: [], types: [DataSourceType.COLLECTION] }
                sourceOrigins[model].sources.push(dataSource.id);
                if (!sourceOrigins[model].types.includes(DataSourceType.COLLECTION)) sourceOrigins[model].types.push(DataSourceType.COLLECTION);
            }
            else if (metadata.type === DataSourceType.WORKFLOW) {
                (dataSource as IWorkflowDataSource).model = model;
                (dataSource as IWorkflowDataSource).action = action;
                dataSource.name = action
                dataSource.origin = model
                if (!sourceOrigins[model]) sourceOrigins[model] = { sources: [], types: [DataSourceType.WORKFLOW] }
                sourceOrigins[model].sources.push(dataSource.id)
                if (!sourceOrigins[model].types.includes(DataSourceType.WORKFLOW)) sourceOrigins[model].types.push(DataSourceType.WORKFLOW)
            }
            else if (metadata.type == DataSourceType.Query) {
                dataSource.name = queryName;
                (dataSource as IQueryDataSource).queryName = queryName;
                (dataSource as IQueryDataSource).queryId = queryId;
                dataSource.origin = SourceOriginQuery
                if (!sourceOrigins[SourceOriginQuery]) sourceOrigins[SourceOriginQuery] = { sources: [], types: [DataSourceType.Query] };
                sourceOrigins[SourceOriginQuery].sources.push(dataSource.id);
            }

            dataSource.history = hasHistoryEnabledForSource(dataSource as IDataSource)
            result.push(dataSource as IDataSource);

            return result;

        }, [] as IDataSource[]);

    return { sources: sources, origins: sourceOrigins }
}

/**
 * Function to perform a text search
 * @param value - The value to be searched
 * @param query - The query to search for
 * @param ignoreCase - Boolean to determine if the search should be case sensitive
 * @returns boolean - Returns true if the query is found in the value, false otherwise
 */
export function textSearch(value: string, query: string, ignoreCase: boolean = false): boolean {
    if (ignoreCase) {
        return value?.toLowerCase().indexOf(query?.toLowerCase()) !== -1;
    } else {
        return value?.indexOf(query) !== -1;
    }
}

export function isSavedQueryOnly(query: IQuery) {
    return (
        query?.sources?.length == 1
        && query?.sources?.[0]?.id?.startsWith('query') // for saved queries 
        && query?.steps?.length == 0
        && query?.sourceOnly
    )
}

/**
 * Function to generate the initial query
 * @param query - Optional query to initialize with
 * @returns IQuery - Returns the initial query
 */
export function generateInitialQuery(query?: IQuery): IQuery {

    const isSavedQuery = isSavedQueryOnly(query)

    return {
        sources: query?.sources || [],
        steps: query?.steps || [],
        sourceOnly: isSavedQuery,
        params: query?.params || []
    };
}

/**
 * generate a function to get the query buy selected step id
 * this will receive query and selectedStepId 
 * if selected id is InitialSourceId get the first source from query , set steps to null 
 * if a selected steps is null return the query 
 * if selected step mathces any step id in steps, get the steps up to that step
 */
export function getQueryBySelectedStepId(query: IQuery, selectedStepId: string): IQuery {
    const _query = generateInitialQuery(query);
    if (!hasValue(selectedStepId)) return _query;

    if (selectedStepId === InitialSourceId) {
        return {
            sources: [_query.sources[0]],
            steps: []
        };
    }

    const stepIndex = _query.steps.findIndex(step => step.id === selectedStepId);
    if (stepIndex === -1) {
        return _query;
    }

    return {
        sources: _query.sources,
        steps: _query.steps.slice(0, stepIndex + 1),
    };
}

export function getQueryForSelectedSource(query: IQuery, selectedSourceId: string): IQuery {
    const _query = generateInitialQuery(query);
    if (!hasValue(selectedSourceId)) return _query;

    const source = _query?.sources?.find(s => s.id === selectedSourceId)
    if (!source) return { sources: [], steps: [] }

    return { sources: [source], steps: [] }
}
/**
 * Function to get the query for the step before the selected step
 * @param query - The query object containing steps
 * @param selectedStepId - The ID of the selected step
 * @returns IQuery - Returns the query for the step before the selected step
 */
export function getQueryUntilPreviousStep(query: IQuery, selectedStepId: string): IQuery {
    const _query = generateInitialQuery(query);
    if (!selectedStepId) return _query;

    if (selectedStepId === InitialSourceId) {
        return {
            sources: [_query.sources[0]],
            steps: []
        };
    }

    const stepIndex = _query.steps.findIndex(step => step.id === selectedStepId);
    if (stepIndex === -1) return _query
    if (stepIndex == 0) return { sources: _query.sources, steps: [] }

    return {
        sources: _query.sources,
        steps: _query.steps.slice(0, stepIndex)
    };
}


/**
 * Function to validate a query
 * This will go through each step and return a boolean indicating if the query is valid
 * @param query - The query object containing steps
 * @returns boolean - Returns true if the query is valid, otherwise false
 */
export function validateQuery(query: IQuery): { isValid: boolean, error?: string | null } {
    if (!query) return { isValid: false, error: 'Invalid query' };
    if (!query?.sources || query?.sources.length === 0) return { isValid: false, error: 'Datasource has not selected' }

    if (query?.steps && query?.steps?.length > 0) {
        for (const step of query.steps) {
            const { isValid } = validateStep(query, step);
            if (!isValid) return { isValid: false, error: `Step ${step.name} is not configured properly` };
        }
    }

    return { isValid: true, error: null };
}
/**
 * Function to get the selected step by its ID
 * @param query - The query object containing steps
 * @param stepId - The ID of the step to be retrieved
 * @returns { step: IStep | null, index: number, previousSteps: IStep[] } - Returns an object containing the step if found, its index, and the previous steps, otherwise null, -1, and an empty array
 */
export function getSelectedStepDetailsById(query: IQuery, stepId: string): { step: IStep | null, index: number, previousSteps: IStep[], selectedSources: IDataSource[] } {
    if (!query || !query.steps || !stepId) return { step: null, index: -1, previousSteps: [], selectedSources: [] };
    const index = query.steps.findIndex(step => step.id === stepId);
    const step = query.steps[index] || null;
    const previousSteps = step ? query.steps.slice(0, index) : [];
    const selectedSources = query?.sources || []
    return { step, index, previousSteps, selectedSources };
}

/**
 * Function to find the source from query sources or steps by source ID
 * @param query - The query object containing sources and steps
 * @param sourceId - The ID of the source to be retrieved
 * @returns { IDataSource | null } - Returns the source object if found, otherwise null
 */
export function findSourceById(query: IQuery, sourceId: string): IDataSource | IStep | null {
    if (!hasValue(sourceId)) return null;

    // Check in sources
    const source = query.sources.find(src => src.id === sourceId);
    if (source) {
        return source;
    }

    const step = query.steps.find(st => st.id === sourceId);
    if (step) {
        return step;
    }
    // If not found, return null
    return null;
}

/**
 * Function to find multiple sources from query sources or steps by an array of source IDs
 * @param query - The query object containing sources and steps
 * @param sourceIds - The array of source IDs to be retrieved
 * @returns { Array<IDataSource | IStep> } - Returns an array of source objects if found, otherwise an empty array
 */
export function findMultipleSourcesByIds(query: IQuery, sourceIds: string[]): Array<IDataSource | IStep> {
    if (!query || !sourceIds || sourceIds.length === 0) return [];

    const sources: Array<IDataSource | IStep> = [];

    for (const sourceId of sourceIds) {
        const source = findSourceById(query, sourceId);
        if (source) sources.push(source);
    }

    return sources;
}


/**
 * Function to find the next step ID based on the current step ID.
 * If there are steps after the current step, select the next one.
 * If not, try to select the step before the current step, or select the initial source.
 * @param query - The query object containing steps
 * @param currentStepId - The ID of the current step
 * @returns string - Returns the next step ID or the initial source ID
 */
export function findNextStepId(query: IQuery, currentStepId: string): string {
    if (!query || !query.steps || query.steps.length === 0) return InitialSourceId;

    const currentIndex = query.steps.findIndex(step => step.id === currentStepId);

    if (currentIndex === -1) return InitialSourceId;

    // If there are steps after the current step, select the next one
    if (currentIndex < query.steps.length - 1) {
        return query.steps[currentIndex + 1].id;
    }

    // If not, try to select the step before the current step
    if (currentIndex > 0) {
        return query.steps[currentIndex - 1].id;
    }

    // If no steps before or after, select the initial source
    return InitialSourceId;
}

export function findNextItemId(items: any, currentIndex: number): string {
    if (items.length - 1 >= currentIndex + 1) return items[currentIndex + 1].id
    return items?.[0]?.id || null
}

/**
 * Function to check if a source is available in query sources
 * @param query - The query object containing sources
 * @param sourceId - The ID of the source to be checked
 * @returns boolean - Returns true if the source is found, otherwise false
 */
export function isSourceAvailableInQuery(query: IQuery, sourceId: string): boolean {
    return query.sources.some(src => src.id === sourceId);
}

export function isDataSource(source: IDataSource | IStep): source is IDataSource {
    if (!source || typeof source !== 'object') return false;
    return 'type' in source && Object.values(DataSourceType).includes((source as IDataSource).type);
}

export function isStep(source: IDataSource | IStep): source is IStep {
    return 'type' in source && Object.values(StepType).includes((source as IStep).type);
}

export function isBefore(step: IStep, sourceStep: IStep, query: IQuery) {
    const sourceIndex = query.steps.findIndex(s => s.id === sourceStep.id)
    const stepIndex = query.steps.findIndex(s => s.id === step.id)

    if (sourceIndex == -1 || stepIndex == -1) return false
    return sourceIndex < stepIndex
}


/**
 * Function to validate step name and id
 * @param step - The step object to be validated
 * @returns Record<string, string> - Returns an object containing validation errors
 */
export function validateStepNameAndId(step: IStep): Record<string, string> {
    const errors: Record<string, string> = {};
    if (!hasValue(step.id)) errors.id = "ID is required";
    if (!hasValue(step.name)) errors.name = "Name is required";
    return errors;
}


export function validateSource(query: IQuery, step: IStep, sourceKey: string, history?: boolean, returnSource?: boolean): { isValid: boolean, error?: string, source?: IDataSource | IStep } {
    if (!step || typeof step !== 'object') {
        return { isValid: false, error: "Step is not configured properly" };
    }

    const sourceId = (step as any)[sourceKey];
    if (!hasValue(sourceId)) {
        return { isValid: false, error: ` ${sourceKey} is required` };
    }

    const source = findSourceById(query, sourceId)

    if (!source) {
        return { isValid: false, error: `Invalid source selected. please select a valid source` };
    }

    if (history && (!isDataSource(source) || !hasHistoryEnabledForSource(source))) return { isValid: false, error: `Select a valid source that has historical data enabled` };

    if (isStep(source) && !isBefore(step, source, query)) return { isValid: false, error: 'Selected source (step) is after the current step. Please select a step before the current step' }

    return { isValid: true, source };
}

/**
 * Function to validate a source in query by sourceId
 * @param query - The query object containing sources
 * @param sourceId - The ID of the source to be validated
 * @returns { isValid: boolean, error?: string } - Returns an object containing validation status and error message if any
 */
export function validateSourceById(query: IQuery, sourceId: string): { isValid: boolean, error?: string } {
    if (!sourceId) {
        return { isValid: false, error: "Source ID is required" };
    }

    if (!findSourceById(query, sourceId)) {
        return { isValid: false, error: `Invalid source selected. please select a valid source` };
    }
    return { isValid: true };
}


/**
 * Function to validate a step
 * @param step - The step object to be validated
 * @returns { isValid: boolean, errors: Record<string, string> } - Returns an object containing validation status and errors
 */
export function validateStep(query: IQuery, step: IStep): IStepValidationResponse {
    if (!step || typeof step !== 'object') return { isValid: false, errors: { step: "step is required" } };

    const registeredStep = getRegisteredStepByType(step.type)
    if (!registeredStep) return { isValid: false, errors: { type: "invalid step type" } }

    return registeredStep.validate(query, step)
}

async function requestSchema(context: IQueryBuilderContext, query: IQuery) {
    try {
        const res = await getSchema(context, query);
        if (!res || !Array.isArray(res)) {
            console.log('Invalid response received for schema request');
            return [];
        }
        return res;
    } catch (e) {
        console.log('Error fetching schema:', e);
        return [];
    }
}

function findSchemaById(schemas: { id: string, schema: ISourceSchema }[], id: string) {
    const ss = (schemas || []).find((s: any) => s.id === id);
    if (ss && ss?.schema && ss.schema.length > 0) return ss.schema
    console.log(`Unable to find a valid schema for the id: ${id}. schemas: ${schemas}`)
    return []
}


export async function getSchemaForQuery(context: IQueryBuilderContext, query: IQuery): Promise<{ id: string, schema: ISourceSchema }[]> {
    const { isValid } = validateQuery(query)
    if (!isValid) {
        console.log('Unable to get the schema for query. Query is not configured properly')
        return []
    }
    return requestSchema(context, query)
}

export async function getOutputSchemaForQuery(context: IQueryBuilderContext, query: IQuery): Promise<ISourceSchema | null> {
    const { isValid } = validateQuery(query)
    if (!isValid) return null

    const schemas = await requestSchema(context, query)
    if (schemas.length == 0) return null

    // lst step 
    const lastStep = query?.steps?.length > 0 ? query.steps[query.steps.length - 1] : query.sources[0]
    if (!lastStep) return null

    const schema = schemas.find(sc => sc.id === lastStep.id)
    return schema?.schema || null
}

export async function getSchemaForSource(context: IQueryBuilderContext, query: IQuery, sourceId: string): Promise<ISourceSchema> {
    // here we dont need to get schema other sources or steps.
    // so just pass the source (data source )
    const source = query?.sources?.find(s => s.id == sourceId)
    if (!source) return []
    const schemas = await requestSchema(context, { sources: [source], steps: [] })
    return findSchemaById(schemas, sourceId)
}

export async function getSchemaForStep(context: IQueryBuilderContext, query: IQuery, stepId: string): Promise<ISourceSchema> {

    const _query = getQueryBySelectedStepId(query, stepId)
    const { step: _step } = getSelectedStepDetailsById(_query, stepId)
    const { isValid } = validateStep(_query, _step)

    if (!isValid) {
        console.log('Unable to get the schema, step is not configured properly')
        return []
    }
    const schemas = await requestSchema(context, query)
    return findSchemaById(schemas, stepId)
}

export function appendSchema(query: IQuery, schema: ISourceSchema, id: string, source?: boolean): IQuery {
    if (source) {
        const sourceIndex = query.sources.findIndex(s => s.id === id);
        if (sourceIndex !== -1) {
            query.sources[sourceIndex].schema = schema;
        } else {
            console.log(`Source with id ${id} not found in query sources.`);
        }
    } else {
        const stepIndex = query.steps.findIndex(s => s.id === id);
        if (stepIndex !== -1) {
            query.steps[stepIndex].schema = schema;
        } else {
            console.log(`Step with id ${id} not found in query steps.`);
        }
    }
    return query;
}

/**
 * Function to update schema for each source and step in a query
 * @param context - The query builder context
 * @param query - The query object containing sources and steps
 * @returns Promise<IQuery> - Returns a promise that resolves to the updated query with schemas
 */
export async function updateSchemasInQuery(context: IQueryBuilderContext, query: IQuery): Promise<IQuery> {
    const { isValid } = validateQuery(query);
    if (!isValid) {
        console.log('Unable to update schemas, query is not configured properly');
        return query;
    }

    const schemas = await requestSchema(context, query);

    // Update schemas for sources
    query.sources = query.sources.map(source => {
        const schema = findSchemaById(schemas, source.id);
        if (schema) {
            source.schema = schema;
        }
        return source;
    });

    // Update schemas for steps
    query.steps = query.steps.map(step => {
        const schema = findSchemaById(schemas, step.id);
        if (schema) {
            step.schema = schema;
        }
        return step;
    });

    return query;
}

export function getUniqueFieldsInSource(query: IQuery, sourceId: string): string[] {
    const source = findSourceById(query, sourceId)
    const fields = new Set(source?.schema?.map(f => (f.name)))
    return Array.from(fields)
}


export function validateFieldConfiguration(query: IQuery, step: IStep, selectedFields: string[], sourceField: string): { isValid: boolean, error?: string } {
    const sourceId = (step as any)[sourceField]
    if (!hasValue(sourceId)) return { isValid: false, error: 'Invalid source field' }

    const source = findSourceById(query, sourceId)
    if (!source) return { isValid: false, error: 'Invalid source selected' }

    const sourceSchema = source.schema || [];
    const schemaFields = sourceSchema.map(field => field?.name);

    const sourceContainsAllFields = selectedFields.every(field => schemaFields.includes(field));

    return { isValid: sourceContainsAllFields, error: sourceContainsAllFields ? null : 'Some of the fields are not available in the source' }
}

export function copyToClipboard(text: string) {
    navigator.clipboard.writeText(text)
}

export async function prepareQuery(context: IQueryBuilderContext, p: any, origins?: FilteredOrigins) {
    if (p.steps && p.sources) {
        let { sources: allSources } = prepareSources((await getAvailableSources(context))?.definitions || [], origins || []);
        for (let i = 0; i < p.sources.length; i++) {
            let s = p.sources[i];
            if (typeof s == 'string') {
                let source = allSources.find(x => x.id == s);
                const schema = await getSchemaForSource(context, { sources: [source], steps: [] }, source.id)
                source.schema = schema
                p.sources[i] = source;
            }
        }
        for (let s of p.steps) {
            (s as IStep).name = s.name || s.id;
            const schema = await getSchemaForStep(context, getQueryBySelectedStepId(p, s.id), s.id)
            s.schema = schema
        }
    }
    return p;
}

export function distinct(array: any[], field: string) {
    const distinctValues = [...new Set(array.map(item => item[field]))];
    return distinctValues;
}

export function validateParam(query: IQuery, param: Param): { isValid: boolean; errors: Record<string, string> } {
    const _query = generateInitialQuery(query);
    const errors: Record<string, string> = {};

    // Validate name presence
    if (!hasValue(param.name)) {
        errors.name = "Parameter name is required.";
    }

    // Validate uniqueness
    const existingNames = _query.params?.map(p => p.name) || [];
    const duplicateCount = existingNames.filter(name => name === param.name).length;
    if (duplicateCount > 1) {
        errors.name = `Duplicate parameter name "${param.name}".`;
    }

    if (!hasValue(param.type)) {
        errors.type = "Parameter type is required";
    }

    return { isValid: Object.keys(errors).length === 0, errors };
}

export function validateParams(query: IQuery) {
    return (query?.params || []).every((p) => {
        const { isValid } = validateParam(query, p)
        return isValid;
    })
}